/**
 * Utility functions for generating and ensuring unique stage IDs
 */

import type { Process, Stage, Task, TaskGroup } from '@/models/flowchart';
import type { Project } from '@/models/project';

/**
 * Generates a unique stage ID using timestamp
 * @param suffix Optional suffix to append to the ID
 * @returns Unique stage ID as string
 */
export function generateStageId(suffix?: string): string {
  const baseId = Date.now().toString();
  return suffix ? `${baseId}-${suffix}` : baseId;
}

/**
 * Generates a unique task ID using timestamp
 * @param suffix Optional suffix to append to the ID
 * @returns Unique task ID as string
 */
export function generateTaskId(suffix?: string): string {
  const baseId = Date.now().toString();
  return suffix ? `${baseId}-${suffix}` : baseId;
}

/**
 * Checks if a stage has a valid ID
 * @param stage The stage to check
 * @returns true if stage has a valid ID, false otherwise
 */
export function hasValidStageId(stage: Stage): boolean {
  return stage.id !== undefined && stage.id !== null && stage.id !== '';
}

/**
 * Checks if a task has a valid ID
 * @param task The task to check
 * @returns true if task has a valid ID, false otherwise
 */
export function hasValidTaskId(task: Task): boolean {
  return task.id !== undefined && task.id !== null && task.id !== '';
}

/**
 * Generates a unique stage ID that doesn't conflict with existing stage IDs in a process
 * @param process The process containing existing stages
 * @param suffix Optional suffix for the ID
 * @returns Unique stage ID
 */
export function generateUniqueStageId(process: Process, suffix?: string): string {
  const existingIds = new Set(
    process.stages
      .filter(hasValidStageId)
      .map(stage => stage.id.toString())
  );
  
  let attempts = 0;
  let newId: string;
  
  do {
    newId = generateStageId(suffix ? `${suffix}${attempts > 0 ? `-${attempts}` : ''}` : undefined);
    attempts++;
  } while (existingIds.has(newId) && attempts < 100); // Prevent infinite loops
  
  return newId;
}

/**
 * Generates a unique task ID that doesn't conflict with existing task IDs in a process
 * @param process The process containing all stages with tasks (for global uniqueness)
 * @param suffix Optional suffix for the ID
 * @returns Unique task ID
 */
export function generateUniqueTaskId(process: Process, suffix?: string): string {
  const existingIds = new Set();
  
  // Collect all existing task IDs from ALL stages in the process for global uniqueness
  process.stages?.forEach(stage => {
    stage.taskGroups?.forEach(group => {
      group.tasks?.forEach(task => {
        if (hasValidTaskId(task)) {
          existingIds.add(task.id.toString());
        }
      });
    });
  });
  
  let attempts = 0;
  let newId: string;
  
  do {
    newId = generateTaskId(suffix ? `${suffix}${attempts > 0 ? `-${attempts}` : ''}` : undefined);
    attempts++;
  } while (existingIds.has(newId) && attempts < 100); // Prevent infinite loops
  
  return newId;
}

/**
 * Ensures a single task has a valid ID, generating one if missing
 * @param task The task to process
 * @param process The parent process (for global unique ID generation)
 * @param index Optional index for generating meaningful suffixes
 * @returns Task with guaranteed valid ID
 */
export function ensureTaskId(task: Task, process: Process, index?: number): Task {
  if (hasValidTaskId(task)) {
    return task;
  }

  // Generate a unique ID for this task across the entire process
  const suffix = index !== undefined ? `${index + 1}` : '1';
  const newId = generateUniqueTaskId(process, suffix);
  
  console.log(`Generated missing task ID: ${newId} for task "${task.name || 'unnamed'}"`);
  
  return {
    ...task,
    id: newId
  };
}

/**
 * Ensures a single stage has a valid ID, generating one if missing
 * Also ensures all tasks within the stage have valid IDs
 * @param stage The stage to process
 * @param process The parent process (for unique ID generation)
 * @param index Optional index for generating meaningful suffixes
 * @returns Stage with guaranteed valid ID and all tasks with valid IDs
 */
export function ensureStageId(stage: Stage, process: Process, index?: number): Stage {
  // First ensure the stage itself has an ID
  let updatedStage = stage;
  if (!hasValidStageId(stage)) {
    const suffix = index !== undefined ? `stage${index + 1}` : 'stage';
    const newId = generateUniqueStageId(process, suffix);
    
    console.log(`Generated missing stage ID: ${newId} for stage "${stage.name || 'unnamed'}"`);
    
    updatedStage = {
      ...stage,
      id: newId
    };
  }

  // Then ensure all tasks within the stage have IDs
  if (updatedStage.taskGroups && Array.isArray(updatedStage.taskGroups)) {
    const updatedTaskGroups = updatedStage.taskGroups.map((group: TaskGroup) => {
      if (group.tasks && Array.isArray(group.tasks)) {
        const updatedTasks = group.tasks.map((task: Task, taskIndex: number) => 
          ensureTaskId(task, process, taskIndex)
        );
        return {
          ...group,
          tasks: updatedTasks
        };
      }
      return group;
    });

    updatedStage = {
      ...updatedStage,
      taskGroups: updatedTaskGroups
    };
  }

  return updatedStage;
}

/**
 * Processes a single process to ensure all its stages and tasks have valid IDs
 * @param process The process to process
 * @returns Process with all stages and tasks having valid IDs
 */
export function ensureAllStageIds(process: Process): Process {
  // Handle case where process or stages might be undefined/null
  if (!process || !process.stages || !Array.isArray(process.stages)) {
    console.warn('Process has no valid stages array:', process);
    return {
      ...process,
      stages: process?.stages || []
    };
  }
  
  // Use a SINGLE timestamp for the entire process and a global counter
  const processTimestamp = Date.now().toString();
  let globalTaskCounter = 1;
  
  const updatedStages = process.stages.map((stage, index) => {
    // First ensure the stage itself has an ID
    let updatedStage = stage;
    if (!hasValidStageId(stage)) {
      const suffix = index !== undefined ? `stage${index + 1}` : 'stage';
      const newId = generateUniqueStageId(process, suffix);
      
      console.log(`Generated missing stage ID: ${newId} for stage "${stage.name || 'unnamed'}"`);
      
      updatedStage = {
        ...stage,
        id: newId
      };
    }

    // Then ensure all tasks within the stage have IDs using single timestamp + global counter
    if (updatedStage.taskGroups && Array.isArray(updatedStage.taskGroups)) {
      const updatedTaskGroups = updatedStage.taskGroups.map((group: TaskGroup) => {
        if (group.tasks && Array.isArray(group.tasks)) {
          const updatedTasks = group.tasks.map((task: Task) => {
            if (hasValidTaskId(task)) {
              return task;
            }

            // Generate unique task ID using SINGLE timestamp + global counter
            const newId = `${processTimestamp}-${globalTaskCounter}`;
            globalTaskCounter++;
            
            console.log(`Generated missing task ID: ${newId} for task "${task.name || 'unnamed'}"`);
            
            return {
              ...task,
              id: newId
            };
          });
          return {
            ...group,
            tasks: updatedTasks
          };
        }
        return group;
      });

      updatedStage = {
        ...updatedStage,
        taskGroups: updatedTaskGroups
      };
    }

    return updatedStage;
  });
  
  return {
    ...process,
    stages: updatedStages
  };
}

/**
 * Processes all processes in a project to ensure all stages and tasks have valid IDs
 * @param project The project to process
 * @returns Project with all stages and tasks having valid IDs
 */
export function ensureAllStageIdsInProject(project: Project): Project {
  // We need to traverse the project structure to find all flowchart data
  // Projects have workspaces -> submenus -> tabs -> data -> processes
  
  const updatedProject = JSON.parse(JSON.stringify(project)); // Deep clone
  
  // Process workspaces structure
  if (updatedProject.workspaces?.submenus) {
    updatedProject.workspaces.submenus = updatedProject.workspaces.submenus.map((submenu: any) => {
      if (submenu.tabs) {
        submenu.tabs = submenu.tabs.map((tab: any) => {
          // Check if this tab has flowchart data with processes
          if (tab.data?.processes && Array.isArray(tab.data.processes)) {
            tab.data.processes = tab.data.processes.map((process: Process) => 
              ensureAllStageIds(process)
            );
          }
          return tab;
        });
      }
      return submenu;
    });
  }
  
  return updatedProject;
}